import React, { useState, useEffect } from "react";
import Axios from "../../_services/caller.service";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import styles from "./AlertsConfigManager.module.scss";
import { toast } from "react-toastify";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";

const sliderColors = {
  firstReminder: "#4CAF50", // Vert
  secondReminder: "#FFC107", // Jaune
  thirdReminder: "#FF5722", // Orange
  overdueReminder: "#F44336", // Rouge
};

const AlertsConfigManager = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Charger la configuration initiale depuis le backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await Axios.get("/getConfig");
        const data = response.data.data;

        // Initialiser tout dans `config`, y compris overdueMin
        setConfig({
          firstReminder: data.firstReminder || 180,
          secondReminder: data.secondReminder || 120,
          thirdReminder: data.thirdReminder || 60,
          overdueReminder: data.overdueReminder || -30,
          overdueMin: data.overdueReminderMin || -365, // Inclure overdueMin dans le state
        });

        setLoading(false);
      } catch (err) {
        setError("Erreur lors du chargement de la configuration.");
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Sauvegarder la configuration
  const handleSave = async () => {
    try {
      // Préparer uniquement les données nécessaires pour le backend
      const updateData = {
        firstReminder: config.firstReminder,
        secondReminder: config.secondReminder,
        thirdReminder: config.thirdReminder,
        overdueReminder: config.overdueReminder,
        overdueReminderMin: config.overdueMin, // Inclure la nouvelle valeur minimale
      };

      // Envoyer la requête de mise à jour partielle
      await Axios.post("/updateConfig", updateData);

      toast.success("Configuration sauvegardée avec succès !");
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde de la configuration.");
    }
  };

  // Gestion des sliders individuels avec contraintes
  const handleSliderChange = (key, value) => {
    setConfig((prevConfig) => {
      const newConfig = { ...prevConfig, [key]: value };

      // Appliquer les contraintes entre les sliders
      if (key === "firstReminder" && value < prevConfig.secondReminder) {
        newConfig.secondReminder = value;
        if (newConfig.secondReminder < prevConfig.thirdReminder) {
          newConfig.thirdReminder = newConfig.secondReminder;
        }
      }

      if (key === "secondReminder" && value < prevConfig.thirdReminder) {
        newConfig.thirdReminder = value;
      }

      if (key === "secondReminder" && value > prevConfig.firstReminder) {
        newConfig.firstReminder = value;
      }

      if (key === "thirdReminder" && value > prevConfig.secondReminder) {
        newConfig.secondReminder = value;
      }

      return newConfig;
    });
  };

  // Gestion spécifique du slider pour overdueMin
  const handleOverdueMinChange = (value) => {
    setConfig((prevConfig) => ({
      ...prevConfig,
      overdueMin: value, // Met à jour la valeur minimale
      overdueReminder: Math.max(prevConfig.overdueReminder, value), // Ajuste overdueReminder si nécessaire
    }));
  };

  if (loading) {
    return <div className={styles.message}>Chargement...</div>;
  }

  if (error) {
    return <div className={`${styles.message} ${styles.error}`}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Gestions des relances</h1>

      {successMessage && (
        <div className={`${styles.message} ${styles.success}`}>
          {successMessage}
        </div>
      )}

      {/* Slider pour la 1ère relance */}
      <div className={styles.sliderWrapper}>
        <h2>1ère relance</h2>
        <Slider
          min={0}
          max={180}
          step={1}
          value={config.firstReminder}
          onChange={(value) => handleSliderChange("firstReminder", value)}
          trackStyle={{ backgroundColor: sliderColors.firstReminder }}
          handleStyle={{ backgroundColor: sliderColors.firstReminder }}
          railStyle={{ backgroundColor: "#E0E0E0" }}
        />
        <span className={styles.value}>
          Déclenchée à : {config.firstReminder} jours avant la calibration
        </span>
      </div>

      {/* Slider pour la 2ème relance */}
      <div className={styles.sliderWrapper}>
        <h2>2ème relance</h2>
        <Slider
          min={0}
          max={config.firstReminder} // Contrainte basée sur la 1ère relance
          step={1}
          value={config.secondReminder}
          onChange={(value) => handleSliderChange("secondReminder", value)}
          trackStyle={{ backgroundColor: sliderColors.secondReminder }}
          handleStyle={{ backgroundColor: sliderColors.secondReminder }}
          railStyle={{ backgroundColor: "#E0E0E0" }}
        />
        <span className={styles.value}>
          Déclenchée à : {config.secondReminder} jours avant la calibration
        </span>
      </div>

      {/* Slider pour la 3ème relance */}
      <div className={styles.sliderWrapper}>
        <h2>3ème relance</h2>
        <Slider
          min={0}
          max={config.secondReminder} // Contrainte basée sur la 2ème relance
          step={1}
          value={config.thirdReminder}
          onChange={(value) => handleSliderChange("thirdReminder", value)}
          trackStyle={{ backgroundColor: sliderColors.thirdReminder }}
          handleStyle={{ backgroundColor: sliderColors.thirdReminder }}
          railStyle={{ backgroundColor: "#E0E0E0" }}
        />
        <span className={styles.value}>
          Déclenchée à : {config.thirdReminder} jours avant la calibration
        </span>
      </div>

      {/* Slider pour le retard */}
      <div className={styles.sliderWrapper}>
        <h2>Retard</h2>
        <Slider
          min={config.overdueMin} // Dynamique basé sur overdueMin
          max={0}
          step={1}
          value={config.overdueReminder}
          onChange={(value) => handleSliderChange("overdueReminder", value)}
          trackStyle={{ backgroundColor: sliderColors.overdueReminder }}
          handleStyle={{ backgroundColor: sliderColors.overdueReminder }}
          railStyle={{ backgroundColor: "#E0E0E0" }}
        />
        <span className={styles.value}>
          Retard déclenché à : {config.overdueReminder} jours après la
          calibration
        </span>
      </div>
      {/* Slider pour la valeur minimale du retard */}
      <div className={styles.sliderWrapper}>
        <h2>Supression du retard</h2>
        <Slider
          min={-365}
          max={config.overdueReminder} // Maximum basé sur overdueReminder
          step={1}
          value={config.overdueMin}
          onChange={handleOverdueMinChange}
          trackStyle={{ backgroundColor: sliderColors.overdueReminder }}
          handleStyle={{ backgroundColor: sliderColors.overdueReminder }}
          railStyle={{ backgroundColor: "#E0E0E0" }}
        />
        <span className={styles.value}>
          Supression automatique dans la db après: {config.overdueMin} jours
          après
        </span>
      </div>
      <PermissionGate actionKey={'manageCertificat.remindersInterval.update'}>
      <button className={styles.button} onClick={handleSave}>
        Sauvegarder
      </button>
      </PermissionGate>
    </div>
  );
};

export default AlertsConfigManager;
